import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import {
  BookOpenText,
  Check,
  CircleDot,
  Cloud,
  CloudOff,
  Home,
  Lightbulb,
  ListTodo,
  Menu,
  Mic,
  Plus,
  RotateCw,
  Settings,
  Sparkles,
  X
} from "lucide-react";
import clsx from "clsx";
import { db } from "./lib/db";
import { ApiError, getAuthStatus, logout } from "./lib/api";
import { isAuthError, isNetworkError, syncNow } from "./lib/sync";
import type {
  DailyFocus,
  Decision,
  Task,
  Thought,
  ViewId,
  WeeklyReview
} from "./types";
import { TodayView } from "./pages/TodayView";
import { TasksView } from "./pages/TasksView";
import { ThinkingView } from "./pages/ThinkingView";
import { ReviewView } from "./pages/ReviewView";
import {
  SettingsView,
  type InstallPromptEvent,
  type SyncState
} from "./pages/SettingsView";
import { QuickCapture } from "./components/QuickCapture";
import { LoginScreen } from "./components/LoginScreen";
import { VoiceOrganizer } from "./components/VoiceOrganizer";
import { Toast } from "./components/ui";

type AuthMode = "loading" | "local" | "login" | "authenticated";
type CaptureType = "thought" | "task" | "decision";

const navItems: Array<{ id: ViewId; label: string; icon: typeof Home }> = [
  { id: "today", label: "今日", icon: Home },
  { id: "tasks", label: "任务", icon: ListTodo },
  { id: "thinking", label: "思考", icon: Lightbulb },
  { id: "review", label: "复盘", icon: BookOpenText }
];

function viewFromHash(): ViewId {
  const value = window.location.hash.replace(/^#\/?/, "");
  return ["today", "tasks", "thinking", "review", "settings"].includes(value)
    ? (value as ViewId)
    : "today";
}

export default function App() {
  const [view, setView] = useState<ViewId>(viewFromHash);
  const [authMode, setAuthMode] = useState<AuthMode>("loading");
  const [apiReachable, setApiReachable] = useState(false);
  const [syncState, setSyncState] = useState<SyncState>({ mode: "local" });
  const [toast, setToast] = useState<{ id: number; message: string; tone: "default" | "error" }>();
  const [capture, setCapture] = useState<{ open: boolean; type: CaptureType }>({
    open: false,
    type: "thought"
  });
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [installPrompt, setInstallPrompt] = useState<InstallPromptEvent | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const toastId = useRef(0);

  const records = useLiveQuery(
    () => db.records.filter((record) => !record.deletedAt).toArray(),
    [],
    []
  );
  const tasks = useMemo<Task[]>(
    () => records.filter((record) => record.kind === "task") as Task[],
    [records]
  );
  const thoughts = useMemo<Thought[]>(
    () => records.filter((record) => record.kind === "thought") as Thought[],
    [records]
  );
  const decisions = useMemo<Decision[]>(
    () => records.filter((record) => record.kind === "decision") as Decision[],
    [records]
  );
  const focuses = useMemo<DailyFocus[]>(
    () => records.filter((record) => record.kind === "dailyFocus") as DailyFocus[],
    [records]
  );
  const reviews = useMemo<WeeklyReview[]>(
    () => records.filter((record) => record.kind === "weeklyReview") as WeeklyReview[],
    [records]
  );

  const notify = useCallback((message: string, tone: "default" | "error" = "default") => {
    toastId.current += 1;
    setToast({ id: toastId.current, message, tone });
  }, []);

  const selectView = useCallback((next: ViewId) => {
    setView(next);
    window.location.hash = `/${next}`;
    setMobileMenuOpen(false);
  }, []);

  const refreshAuth = useCallback(async () => {
    try {
      const status = await getAuthStatus();
      setApiReachable(true);
      if (!status.configured) {
        setAuthMode("local");
        setSyncState({
          mode: "local",
          message: "云端认证尚未配置。可以继续使用本地模式。"
        });
      } else if (status.authenticated) {
        setAuthMode("authenticated");
      } else {
        setAuthMode("login");
      }
    } catch (error) {
      setApiReachable(false);
      setAuthMode("local");
      setSyncState({
        mode: navigator.onLine ? "local" : "offline",
        message: navigator.onLine
          ? "未连接云端，当前记录安全保存在本机。"
          : "当前离线，记录会先保存在本机。"
      });
    }
  }, []);

  const runSync = useCallback(async () => {
    if (authMode !== "authenticated") return;
    setSyncState((current) => ({ ...current, mode: "syncing", message: undefined }));
    try {
      const result = await syncNow();
      const now = new Date().toISOString();
      setSyncState({
        mode: "synced",
        lastSyncedAt: now,
        message:
          result.conflicts > 0
            ? `已处理 ${result.conflicts} 个同步冲突，并保留冲突副本。`
            : undefined
      });
    } catch (error) {
      if (isAuthError(error)) {
        setAuthMode("login");
        setSyncState({ mode: "local", message: "云端登录已过期，请重新登录。" });
      } else if (isNetworkError(error) || !navigator.onLine) {
        setSyncState({
          mode: "offline",
          message: "当前无法连接云端，记录会继续保存在本机。"
        });
      } else {
        setSyncState({
          mode: "error",
          message:
            error instanceof ApiError ? error.message : "同步失败，本地记录不受影响。"
        });
      }
    }
  }, [authMode]);

  async function signOut() {
    try {
      await logout();
    } catch {
      // 本地状态仍要退出，云端会话会在过期后失效。
    }
    setAuthMode("login");
    setSyncState({ mode: "local", message: "已退出云端同步。" });
  }

  useEffect(() => {
    const onHashChange = () => setView(viewFromHash());
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  useEffect(() => {
    void refreshAuth();
  }, [refreshAuth]);

  useEffect(() => {
    if (authMode !== "authenticated") return;
    void runSync();
    const interval = window.setInterval(() => void runSync(), 30_000);
    const onOnline = () => void runSync();
    const onLocalChange = () => {
      window.clearTimeout((onLocalChange as typeof onLocalChange & { timer?: number }).timer);
      (onLocalChange as typeof onLocalChange & { timer?: number }).timer = window.setTimeout(
        () => void runSync(),
        1200
      );
    };
    window.addEventListener("online", onOnline);
    window.addEventListener("workbench:local-change", onLocalChange);
    const onVisibility = () => document.visibilityState === "visible" && void runSync();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("workbench:local-change", onLocalChange);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [authMode, runSync]);

  useEffect(() => {
    if (authMode === "local" && navigator.onLine) {
      const onWake = () => void refreshAuth();
      window.addEventListener("online", onWake);
      return () => window.removeEventListener("online", onWake);
    }
  }, [authMode, refreshAuth]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        if (event.shiftKey) {
          setVoiceOpen(true);
        } else {
          setCapture({ open: true, type: "thought" });
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    const onBeforeInstall = (event: Event) => {
      event.preventDefault();
      setInstallPrompt(event as InstallPromptEvent);
    };
    const onInstalled = () => setInstallPrompt(null);
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    void navigator.storage?.persist?.();
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (authMode === "loading") {
    return (
      <main className="boot-screen">
        <div className="login-mark">T</div>
        <p>正在打开你的工作台…</p>
      </main>
    );
  }

  if (authMode === "login") {
    return (
      <LoginScreen
        onAuthenticated={() => {
          setAuthMode("authenticated");
          setSyncState({ mode: "syncing" });
        }}
      />
    );
  }

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <button type="button" className="brand" onClick={() => selectView("today")}>
          <span className="brand-mark">T</span>
          <span>
            <strong>态格创始人工作台</strong>
            <small>记录 · 聚焦 · 复盘</small>
          </span>
        </button>
        <nav className="desktop-nav" aria-label="主导航">
          {navItems.map((item) => (
            <button
              key={item.id}
              type="button"
              className={clsx("nav-item", view === item.id && "active")}
              onClick={() => selectView(item.id)}
            >
              <item.icon size={19} />
              <span>{item.label}</span>
              {item.id === "tasks" && tasks.filter((task) => task.status === "inbox").length > 0 && (
                <span className="nav-count">{tasks.filter((task) => task.status === "inbox").length}</span>
              )}
            </button>
          ))}
        </nav>
        <div className="sidebar-tools">
          <button
            type="button"
            className="sidebar-capture"
            onClick={() => setCapture({ open: true, type: "thought" })}
          >
            <Plus size={19} />
            <span>随手记</span>
            <kbd>⌘K</kbd>
          </button>
          <button
            type="button"
            className="sidebar-capture"
            onClick={() => setVoiceOpen(true)}
          >
            <Mic size={19} />
            <span>语音整理</span>
            <kbd>⇧⌘K</kbd>
          </button>
        </div>
        <div className="sidebar-bottom">
          <div className={clsx("sync-indicator", syncState.mode)}>
            {syncState.mode === "syncing" ? (
              <RotateCw size={15} className="spin" />
            ) : syncState.mode === "synced" ? (
              <Check size={15} />
            ) : syncState.mode === "offline" ? (
              <CloudOff size={15} />
            ) : (
              <Cloud size={15} />
            )}
            <span>{syncSidebarLabel(syncState.mode)}</span>
          </div>
          <button
            type="button"
            className={clsx("nav-item settings-link", view === "settings" && "active")}
            onClick={() => selectView("settings")}
          >
            <Settings size={18} />
            <span>设置与数据</span>
          </button>
        </div>
      </aside>

      <header className="mobile-header">
        <button type="button" className="brand-mobile" onClick={() => selectView("today")}>
          <span className="brand-mark">T</span>
          <span>创始人工作台</span>
        </button>
        <div className="mobile-header-actions">
          <button type="button" className="icon-button" onClick={() => setVoiceOpen(true)} aria-label="语音整理">
            <Mic size={21} />
          </button>
          <span className={clsx("sync-dot", syncState.mode)} title={syncSidebarLabel(syncState.mode)} />
          <button type="button" className="icon-button" onClick={() => setMobileMenuOpen((value) => !value)} aria-label="打开菜单">
            {mobileMenuOpen ? <X size={21} /> : <Menu size={21} />}
          </button>
        </div>
      </header>

      {mobileMenuOpen && (
        <div className="mobile-menu">
          <button type="button" onClick={() => selectView("settings")}>
            <Settings size={18} /> 设置与数据
          </button>
          <button type="button" onClick={() => setCapture({ open: true, type: "thought" })}>
            <Sparkles size={18} /> 随手记
          </button>
          <button type="button" onClick={() => setVoiceOpen(true)}>
            <Mic size={18} /> 语音整理
          </button>
        </div>
      )}

      <main className="main-area">
        {view === "today" && (
          <TodayView
            tasks={tasks}
            thoughts={thoughts}
            decisions={decisions}
            focuses={focuses}
            onOpenCapture={(type = "thought") => setCapture({ open: true, type })}
            onOpenVoice={() => setVoiceOpen(true)}
            onNavigate={selectView}
            notify={notify}
          />
        )}
        {view === "tasks" && <TasksView tasks={tasks} notify={notify} />}
        {view === "thinking" && (
          <ThinkingView thoughts={thoughts} decisions={decisions} notify={notify} />
        )}
        {view === "review" && (
          <ReviewView
            tasks={tasks}
            thoughts={thoughts}
            decisions={decisions}
            focuses={focuses}
            reviews={reviews}
            onNavigate={selectView}
            notify={notify}
          />
        )}
        {view === "settings" && (
          <SettingsView
            records={records}
            syncState={syncState}
            authenticated={authMode === "authenticated"}
            installPrompt={installPrompt}
            onSync={() => void runSync()}
            onLogout={() => void signOut()}
            notify={notify}
          />
        )}
      </main>

      <nav className="bottom-nav" aria-label="手机主导航">
        {navItems.map((item) => (
          <button
            key={item.id}
            type="button"
            className={clsx(view === item.id && "active")}
            onClick={() => selectView(item.id)}
          >
            <item.icon size={20} />
            <span>{item.label}</span>
          </button>
        ))}
      </nav>
      <button
        type="button"
        className="mobile-capture"
        onClick={() => setCapture({ open: true, type: "thought" })}
        aria-label="随手记录"
      >
        <Plus size={24} />
      </button>

      {capture.open && (
        <QuickCapture
          initialType={capture.type}
          onClose={() => setCapture((current) => ({ ...current, open: false }))}
          onSaved={notify}
        />
      )}

      {voiceOpen && (
        <VoiceOrganizer
          onClose={() => setVoiceOpen(false)}
          notify={notify}
        />
      )}

      {toast && (
        <Toast
          key={toast.id}
          message={toast.message}
          tone={toast.tone}
          onClose={() => setToast(undefined)}
        />
      )}

      {!apiReachable && (
        <div className="local-mode-note" title="当前记录保存在本机">
          <CircleDot size={13} /> 本地
        </div>
      )}
    </div>
  );
}

function syncSidebarLabel(mode: SyncState["mode"]): string {
  return {
    local: "本地保存",
    syncing: "同步中",
    synced: "已同步",
    offline: "离线保存",
    error: "同步待重试"
  }[mode];
}
